import { useAuth } from '@berky/auth'
import type { ModuleConfig } from '@berky/module-contract'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { matchRoutes, useLocation, type RouteObject } from 'react-router'

import { moduleAccess } from '../module-access'
import { modules } from '../modules'
import { usePlatformUsageApi, type UsageSettings } from './api'

const shellRoutes: RouteObject[] = [
  { path: '/' },
  { path: '/verwaltung' },
  { path: '/module-navigation' },
  { path: '/nutzungsanalyse' },
  { path: '/benachrichtigungen' },
  { path: '/feedback/:id?' },
  { path: '/mein-feedback/:id?' },
  { path: '/forbidden' },
]

/** Only static route templates leave the browser; no IDs, query strings or document titles. */
export function routeTemplate(routes: RouteObject[], pathname: string): string | null {
  const matches = matchRoutes(routes, pathname)
  if (!matches) return null
  const segments = matches
    .flatMap((match) =>
      (match.route.path ?? '')
        .split('/')
        .filter(
          (segment) =>
            !(
              segment.startsWith(':') &&
              segment.endsWith('?') &&
              match.params[segment.slice(1, -1)] === undefined
            ),
        ),
    )
    .filter(Boolean)
    .map((segment) =>
      segment.startsWith(':') ? ':parameter' : segment.replace(/\?$/, ''),
    )
  return `/${segments.join('/')}`
}

async function resolvePage(pathname: string, module?: ModuleConfig) {
  if (!module) {
    const pagePath = routeTemplate(shellRoutes, pathname)
    return pagePath === null ? null : { moduleId: 'platform', pagePath }
  }
  const routes = await module.routes()
  const pagePath = routeTemplate(
    routes.default,
    pathname.slice(module.id.length + 1) || '/',
  )
  return pagePath === null ? null : { moduleId: module.id, pagePath }
}

/** Foreground time is an estimate, capped after one minute without interaction. */
export class ActiveTime {
  private sampledAt: number
  private interactedAt: number
  private milliseconds = 0
  private foreground: boolean

  constructor(now: number, foreground: boolean) {
    this.sampledAt = now
    this.interactedAt = now
    this.foreground = foreground
  }

  sample(now: number) {
    if (this.foreground) {
      this.milliseconds += Math.max(
        0,
        Math.min(now, this.interactedAt + 60_000) - this.sampledAt,
      )
    }
    this.sampledAt = now
    return Math.min(86_400, Math.floor(this.milliseconds / 1000))
  }

  interact(now: number) {
    this.sample(now)
    this.interactedAt = now
  }

  setForeground(now: number, foreground: boolean) {
    this.sample(now)
    this.foreground = foreground
    if (foreground) this.interactedAt = now
  }
}

export function PageUsageTracker() {
  const { user } = useAuth()
  const api = usePlatformUsageApi()
  const { pathname } = useLocation()
  const module = modules.find((candidate) => candidate.id === pathname.split('/')[1])
  const allowed = !!user && (!module || moduleAccess(user.roles, module) === 'erlaubt')
  const policy = useQuery({
    queryKey: ['platform-usage-settings'],
    queryFn: ({ signal }) => api.get<UsageSettings>('/settings', { signal }),
    enabled: !!user,
    refetchOnWindowFocus: true,
  })
  const version = policy.data?.testMode ? policy.data.version : null

  useEffect(() => {
    if (!allowed || !version) return
    let cancelled = false
    let dispose: (() => void) | undefined
    // Resolving through the public route contract also avoids counting StrictMode's discarded effect.
    void resolvePage(pathname, module)
      .then((page) => {
        if (cancelled || !page) return
        const id = crypto.randomUUID()
        const clock = new ActiveTime(
          performance.now(),
          document.visibilityState === 'visible' && document.hasFocus(),
        )
        let sentSeconds = -1
        let sending = false
        const flush = () => {
          const activeSeconds = clock.sample(performance.now())
          if (sending || sentSeconds === activeSeconds) return
          sending = true
          void api
            .post(
              `/visits/${id}`,
              { ...page, activeSeconds, policyVersion: version },
              {
                signal: AbortSignal.timeout(10_000),
              },
            )
            .then(() => {
              sentSeconds = activeSeconds
            })
            .catch(() => undefined)
            .finally(() => {
              sending = false
            })
        }
        const interact = () => clock.interact(performance.now())
        const visibility = () => {
          clock.setForeground(
            performance.now(),
            document.visibilityState === 'visible' && document.hasFocus(),
          )
          flush()
        }
        const events = ['pointerdown', 'keydown', 'scroll'] as const
        events.forEach((event) =>
          window.addEventListener(event, interact, { passive: true, capture: true }),
        )
        document.addEventListener('visibilitychange', visibility)
        window.addEventListener('focus', visibility)
        window.addEventListener('blur', visibility)
        window.addEventListener('pagehide', flush)
        const interval = window.setInterval(flush, 15_000)
        flush()
        dispose = () => {
          window.clearInterval(interval)
          events.forEach((event) =>
            window.removeEventListener(event, interact, { capture: true }),
          )
          document.removeEventListener('visibilitychange', visibility)
          window.removeEventListener('focus', visibility)
          window.removeEventListener('blur', visibility)
          window.removeEventListener('pagehide', flush)
          flush()
        }
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
      dispose?.()
    }
  }, [allowed, api, module, pathname, user?.id, version])

  return null
}
